import { hasOpenRiserSides } from './blissStairSides.js'
import { finishedUpgradePrice } from './blissUpgradePricing.js'

export const ELECTRICAL_HOURLY_RATE = 150
/** Owner-supplied purchasing allowances, not complete add-on costs or retail prices. */
export const BEDSIDE_MATERIAL_COSTS = {
  undermountDrawerSlides: {
    cost: 50,
    // Confirm whether one purchased unit is a pair or an individual slide.
    purchaseUnit: null as 'pair' | 'individual_slide' | null,
  },
  // Owner-confirmed September 29. Internal purchasing reference, not customer copy.
  twoSidedMelamineBalticBirch: {
    sheetWidthInches: 48,
    sheetLengthInches: 96,
    costPerSheet: 90,
    // Thickness and nesting yield remain unconfirmed.
    costPerSquareFoot: 90 / (4 * 8),
  },
  uvCoatedPlywood: {
    sheetWidthInches: 48,
    sheetLengthInches: 96,
    costPerSheet: 120,
    // $3.75/sq ft before cutting waste; thickness and takeoff remain to be confirmed.
    costPerSquareFoot: 120 / (4 * 8),
  },
} as const

export const BEDSIDE_OPTIONS = [
  {
    id: 'nightstand',
    label: 'Freestanding nightstand',
    description: 'Beside the lower bunk, between the bed and wall or staircase.',
  },
  {
    id: 'nightstand_drawers',
    label: 'Nightstand with two drawers',
    description:
      'Two small bedside drawers. On perpendicular layouts, they pull out along the lower bunk’s length.',
  },
  {
    id: 'stair_cubby',
    label: 'Staircase bedside cubby',
    description: 'An opening in the staircase side facing the lower-bunk sleeper.',
  },
  {
    id: 'stair_cubby_shelf',
    label: 'Staircase cubby with one shelf',
    description: 'A tall bedside cubby divided into two spaces for books and electronics.',
  },
  {
    id: 'wall_niche',
    label: 'Wood-lined wall niche (Bliss Custom)',
    description:
      'Bliss Custom only. A recess between wall studs, lined with decorative veneer or thin plywood.',
  },
  {
    id: 'wall_niche_prefab',
    label: 'Prefabricated wall niche (Bliss Custom)',
    description:
      'Bliss Custom only. A repurposed shower niche as an alternative to a wood-lined box.',
  },
  {
    id: 'reading_sconce',
    label: 'Reading sconce',
    description: 'An individual reading light beside the selected sleeping spot.',
  },
  {
    id: 'charging_outlet',
    label: 'Bedside charging outlet',
    description:
      'A charging provision beside the selected sleeping spot. Hardware and power connection are confirmed with your designer.',
  },
] as const
export type BedsideKind = (typeof BEDSIDE_OPTIONS)[number]['id']
export type BedsidePower = 'confirm' | 'hardwired' | 'plug_in'
export type BedsideSelection = {
  id: string
  kind: BedsideKind
  bedId: string
  /** Paired Custom configurations contain two complete bunk sets. */
  bunkIndex: 0 | 1
  level: 'lower' | 'upper'
  quantity: number
  /** A separate staircase ID, or the Custom assembly ID for its built-in stairs. */
  stairId?: string
  power?: BedsidePower
  placement?: 'wall_side' | 'stair_side' | 'between_bunks'
  /** Preferred position along the lower bunk, 0 = pillow end, 100 = foot end. */
  nightstandAlong?: number
  /** Omit width for automatic fit; legacy nightstands retain their feet. */
  nightstandWidth?: number
  nightstandFeet?: boolean
  /** Designer-reviewed placement in front of a window; doors and structure still block. */
  allowWindowOverlap?: boolean
  studSpacing?: 16 | 24
}
export type BedsideBed = {
  id: string
  name: string
  kind?: string
  customStyle?: string
  stairStyle?: string
  servesBedIds?: string[]
}
export type ElectricalHours = { factory: number; onsite: number }
export type BedsideUnitPrice = {
  rawMaterials: number | null
  electricalHours?: ElectricalHours | null
}
export type BedsidePricing = {
  units: Partial<Record<BedsideKind, BedsideUnitPrice>>
  poweredUnits: Partial<
    Record<
      'reading_sconce' | 'charging_outlet',
      Partial<Record<Exclude<BedsidePower, 'confirm'>, BedsideUnitPrice>>
    >
  >
  /** Connection/setup shared by all electrical selections in one room, never per fixture. */
  sharedElectricalHours: Partial<Record<Exclude<BedsidePower, 'confirm'>, ElectricalHours | null>>
}

// Component allowances above are not complete unit costs. Takeoff, other hardware,
// labor allowances and electrical hardware remain pending. Null means pending, never free.
// Set confirmed raw materials and factory/on-site hours here; clients never supply a quote amount.
export const BEDSIDE_PRICING: BedsidePricing = {
  units: {
    nightstand: { rawMaterials: null },
    nightstand_drawers: { rawMaterials: null },
    stair_cubby: { rawMaterials: null },
    stair_cubby_shelf: { rawMaterials: null },
    wall_niche: { rawMaterials: null },
  },
  poweredUnits: {},
  sharedElectricalHours: {},
}

export const bedsideOption = (kind: BedsideKind) => BEDSIDE_OPTIONS.find(o => o.id === kind)!
export const isWallNiche = (kind: BedsideKind) =>
  kind === 'wall_niche' || kind === 'wall_niche_prefab'
export const requiresCustomBedside = (selections: BedsideSelection[] = []) =>
  selections.some(s => isWallNiche(s.kind))
export function wallNicheFraming(studSpacing: 16 | 24, wallThickness: 4.5 | 6.5) {
  const width = studSpacing - 1.5
  return { width, height: width * 1.5, studDepth: wallThickness - 1 }
}
export const isNightstand = (kind: BedsideKind) =>
  kind === 'nightstand' || kind === 'nightstand_drawers'
export const isCubby = (kind: BedsideKind) => kind === 'stair_cubby' || kind === 'stair_cubby_shelf'
export const isElectrical = (kind: BedsideKind) =>
  kind === 'reading_sconce' || kind === 'charging_outlet'
export function bedsideBunkCount(bed: BedsideBed): 1 | 2 {
  return bed.kind === 'bridge' ||
    (bed.kind === 'custom' && ['inlet', 'rosemary'].includes(bed.customStyle ?? ''))
    ? 2
    : 1
}
export function bedsideStairs(bed: BedsideBed, beds: BedsideBed[]) {
  return bed.kind === 'bridge' ||
    (bed.kind === 'custom' && ['rosemary', 'watercolor', 'scenic'].includes(bed.customStyle ?? ''))
    ? [bed]
    : beds.filter(stair => stair.kind === 'stairs' && stair.servesBedIds?.includes(bed.id))
}
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)
export function isBedsideSelections(value: unknown): value is BedsideSelection[] {
  if (!Array.isArray(value) || value.length > 120) return false
  const ids = new Set<string>()
  return value.every(s => {
    if (
      !record(s) ||
      typeof s.id !== 'string' ||
      !s.id ||
      s.id.length > 128 ||
      ids.has(s.id) ||
      !BEDSIDE_OPTIONS.some(o => o.id === s.kind) ||
      typeof s.bedId !== 'string' ||
      !s.bedId ||
      s.bedId.length > 128 ||
      ![0, 1].includes(s.bunkIndex as number) ||
      !['lower', 'upper'].includes(s.level as string) ||
      !Number.isInteger(s.quantity) ||
      (s.quantity as number) < 1 ||
      (s.quantity as number) > 8 ||
      (s.stairId !== undefined &&
        (typeof s.stairId !== 'string' || !s.stairId || s.stairId.length > 128)) ||
      (s.power !== undefined && !['confirm', 'hardwired', 'plug_in'].includes(s.power as string))
    )
      return false
    if (isCubby(s.kind as BedsideKind) && (s.level !== 'lower' || !s.stairId)) return false
    if (
      !isCubby(s.kind as BedsideKind) &&
      !(
        (isNightstand(s.kind as BedsideKind) || isElectrical(s.kind as BedsideKind)) &&
        ['stair_side', 'between_bunks'].includes(s.placement as string)
      ) &&
      s.stairId !== undefined
    )
      return false
    if (
      isNightstand(s.kind as BedsideKind) &&
      (s.level !== 'lower' ||
        !['wall_side', 'stair_side', 'between_bunks'].includes(s.placement as string))
    )
      return false
    if (isElectrical(s.kind as BedsideKind)) {
      if (s.placement !== undefined && !['wall_side','stair_side'].includes(s.placement as string)) return false
      if (s.placement === 'stair_side' && (s.level !== 'lower' || !s.stairId)) return false
    } else if (!isNightstand(s.kind as BedsideKind) && s.placement !== undefined) return false
    if (
      s.nightstandAlong !== undefined &&
      (!isNightstand(s.kind as BedsideKind) ||
        typeof s.nightstandAlong !== 'number' ||
        !Number.isFinite(s.nightstandAlong) ||
        s.nightstandAlong < 0 ||
        s.nightstandAlong > 100)
    )
      return false
    if (
      s.nightstandWidth !== undefined &&
      (!isNightstand(s.kind as BedsideKind) ||
        typeof s.nightstandWidth !== 'number' ||
        !Number.isFinite(s.nightstandWidth) ||
        s.nightstandWidth <= 0 ||
        s.nightstandWidth > 720)
    )
      return false
    if (
      s.allowWindowOverlap !== undefined &&
      (!isNightstand(s.kind as BedsideKind) || typeof s.allowWindowOverlap !== 'boolean')
    )
      return false
    if (
      s.nightstandFeet !== undefined &&
      (!isNightstand(s.kind as BedsideKind) || typeof s.nightstandFeet !== 'boolean')
    )
      return false
    if (
      isWallNiche(s.kind as BedsideKind)
        ? ![16, 24].includes(s.studSpacing as number)
        : s.studSpacing !== undefined
    )
      return false
    if (!isElectrical(s.kind as BedsideKind) && s.power !== undefined) return false
    ids.add(s.id)
    return true
  })
}
export function hasBetweenBunksSpace(bed: BedsideBed) {
  return (
    bedsideBunkCount(bed) === 2 &&
    bedsideStairs(bed, []).some(stair => stair.id === bed.id && (stair.stairStyle === 'stringer' || hasOpenRiserSides(stair)))
  )
}
export function bedsideSelectionIssue(s: BedsideSelection, beds: BedsideBed[]): string | null {
  const bed = beds.find(b => b.id === s.bedId && b.kind !== 'stairs')
  if (!bed || s.bunkIndex >= bedsideBunkCount(bed)) return 'Choose a bunk for this add-on.'
  if (
    (isCubby(s.kind) ||
      ((isNightstand(s.kind) || isElectrical(s.kind)) && ['stair_side', 'between_bunks'].includes(s.placement ?? ''))) &&
    (s.level !== 'lower' || !bedsideStairs(bed, beds).some(stair => stair.id === s.stairId))
  )
    return 'Choose a staircase serving this lower bunk for this bedside add-on.'
  if (isNightstand(s.kind) && s.placement === 'between_bunks' && !hasBetweenBunksSpace(bed))
    return 'Between lower bunks needs a paired layout with open risers or exposed stringers. Choose another position or switch the staircase style.'
  if ((isCubby(s.kind) || (isElectrical(s.kind) && s.placement === 'stair_side')) && beds.find(b => b.id === s.stairId)?.stairStyle === 'stringer')
    return 'This add-on needs a solid staircase side; exposed stringers have no side enclosure.'
  return null
}
export function bedsideTargetLabel(s: BedsideSelection, beds: BedsideBed[]) {
  const bunks = beds.filter(b => b.kind !== 'stairs'),
    index = bunks.findIndex(b => b.id === s.bedId),
    bed = bunks[index]
  if (bed && s.placement === 'between_bunks') return `Bunk ${index + 1} · both lower sleepers`
  return bed
    ? `Bunk ${index + 1}${bedsideBunkCount(bed) === 2 ? ` · set ${s.bunkIndex + 1}` : ''} · ${s.level} sleeper`
    : 'Bunk needs selection'
}
export function bedsideDescription(s: BedsideSelection, beds: BedsideBed[]) {
  const power = isElectrical(s.kind)
    ? ` · ${s.power === 'hardwired' ? 'hardwired' : s.power === 'plug_in' ? 'plug-in' : 'power connection to confirm'}`
    : ''
  return `${s.quantity} × ${bedsideOption(s.kind).label} · ${bedsideTargetLabel(s, beds)}${isCubby(s.kind) ? ' · opens toward lower bunk' : ''}${isNightstand(s.kind) ? `${s.placement === 'between_bunks' ? ' · between lower bunks beneath the stairs' : s.placement === 'stair_side' ? ' · between bed and staircase' : ' · between bed and wall'} · ${s.nightstandWidth ? `${s.nightstandWidth}″ custom width` : 'width fitted to space'} · ${s.nightstandFeet === false ? 'without feet' : 'with feet'}` : ''}${isWallNiche(s.kind as BedsideKind) ? ` · ${s.studSpacing}″ on-center studs · ${s.studSpacing! - 1.5}″ × ${(s.studSpacing! - 1.5) * 1.5}″ framing bay before lining` : ''}${selectionDrawerNote(s, beds)}${isElectrical(s.kind) && s.placement === 'stair_side' ? ' · mounted on staircase side facing sleeper' : ''}${power}${isNightstand(s.kind) && s.allowWindowOverlap ? ' · designer allows window overlap; review window access and operation' : ''}`
}
function selectionDrawerNote(s: BedsideSelection, beds: BedsideBed[]) {
  const bed = beds.find(b => b.id === s.bedId)
  return s.kind === 'nightstand_drawers' &&
    (bed?.kind === 'bridge' || ['rosemary', 'watercolor'].includes(bed?.customStyle ?? ''))
    ? ' · drawers pull along the lower bunk’s length'
    : ''
}
export type BedsideQuoteLine = {
  key: string
  label: string
  detail?: string
  price: number
  pending?: boolean
}
const validCost = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0
function validHours(hours: ElectricalHours | null | undefined): hours is ElectricalHours {
  return !!hours && validCost(hours.factory) && validCost(hours.onsite)
}
/** Shared by the studio and authoritative server quote. Pending amounts do not count as $0 upgrades. */
export function priceBedsideAddons(
  selections: BedsideSelection[] = [],
  beds: BedsideBed[],
  pricing: BedsidePricing = BEDSIDE_PRICING
) {
  if (!isBedsideSelections(selections))
    throw new Error('Review your bedside add-on selections before pricing.')
  const lines: BedsideQuoteLine[] = []
  const connections = new Set<Exclude<BedsidePower, 'confirm'>>()
  let factory = 0,
    onsite = 0,
    electricalPending = false
  for (const selection of selections) {
    const electrical = isElectrical(selection.kind),
      power = selection.power
    const unit = electrical
      ? power && power !== 'confirm'
        ? pricing.poweredUnits[selection.kind as 'reading_sconce' | 'charging_outlet']?.[power]
        : undefined
      : pricing.units[selection.kind]
    const issue = bedsideSelectionIssue(selection, beds)
    const pending = !!issue || !unit || !validCost(unit.rawMaterials)
    lines.push({
      key: `bedside-${selection.id}`,
      label: bedsideOption(selection.kind).label,
      detail: issue ?? bedsideDescription(selection, beds),
      price: pending ? 0 : finishedUpgradePrice(unit!.rawMaterials!) * selection.quantity,
      pending,
    })
    if (electrical) {
      if (power && power !== 'confirm') connections.add(power)
      if (issue || !validHours(unit?.electricalHours)) electricalPending = true
      else {
        factory += unit.electricalHours.factory * selection.quantity
        onsite += unit.electricalHours.onsite * selection.quantity
      }
    }
  }
  if (selections.some(s => isElectrical(s.kind))) {
    // Mixed plug-in/hardwired fixtures share the same room setup. Use the
    // hardwired allowance when present, otherwise plug-in; never sum both.
    for (const power of connections.size
      ? [connections.has('hardwired') ? ('hardwired' as const) : ('plug_in' as const)]
      : []) {
      const hours = pricing.sharedElectricalHours[power]
      if (!validHours(hours)) electricalPending = true
      else {
        factory += hours.factory
        onsite += hours.onsite
      }
    }
    if (electricalPending)
      lines.push({
        key: 'bedside-electrical',
        label: 'Electrical installation allowance',
        detail:
          'Factory preparation and on-site work to be confirmed; shared room wiring counted once.',
        price: 0,
        pending: true,
      })
    else {
      for (const [key, label, hours] of [
        ['factory', 'Factory electrical preparation', factory],
        ['onsite', 'On-site electrical work', onsite],
      ] as const)
        if (hours)
          lines.push({
            key: `bedside-electrical-${key}`,
            label,
            detail: `${hours} hours × $${ELECTRICAL_HOURLY_RATE}/hour · includes shared room setup`,
            price: Math.round(hours * ELECTRICAL_HOURLY_RATE * 100) / 100,
          })
    }
  }
  return {
    lines,
    total: lines.reduce((n, line) => n + line.price, 0),
    pending: lines.some(l => l.pending),
  }
}


